import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import RadioGroup from '@cloudscape-design/components/radio-group';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Toggle from '@cloudscape-design/components/toggle';
import TokenGroup from '@cloudscape-design/components/token-group';
import {
  ALL_API_SCOPES,
  AllowedIpSchema,
  ApiTenantScope,
  API_DEFAULT_REQUESTS_PER_MINUTE,
  API_MAX_REQUESTS_PER_MINUTE,
  API_MIN_REQUESTS_PER_MINUTE,
  MAX_DEMO_TENANTS_PER_KEY,
  MAX_PORTAL_EMAILS_PER_KEY,
  PortalEmailSchema,
  type ApiKey,
  type ApiScope,
  type CreateApiKeyResult,
  type CrmPartner,
  type Tenant,
} from '@etiquetachef/types';
import { adminCreateApiKeyCallable, adminUpdateApiKeyCallable } from '@/lib/callables';
import { listCrmPartners } from '@/lib/firestoreCrmPartners';
import { listDemoTenants } from '@/lib/firestoreTenants';
import { reportNonFatal } from '@/lib/errorReport';
import { toast } from '@/lib/notifications';

const MAX_IPS = 20;

/**
 * Rótulo i18n de um scope. As chaves NÃO podem conter `:` — o i18next trata
 * dois-pontos como separador de namespace, então `scopes.tenants:read` era
 * resolvido como namespace `…scopes.tenants` + chave `read`, e a UI mostrava
 * literalmente "read" em toda a lista de permissões.
 */
function scopeKey(scope: ApiScope): string {
  const [resource] = scope.split(':');
  return `admin.apiKeys.scopes.${resource ?? ''}Read`;
}

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  apiKey: ApiKey | null;
  onClose: () => void;
  /** Chamado com as credenciais recém-criadas — só no modo `create`. */
  onCreated: (credentials: CreateApiKeyResult) => void;
};

type FieldErrors = {
  name?: string;
  partner?: string;
  scopePartner?: string;
  demoTenants?: string;
  scopes?: string;
  ip?: string;
  portalEmail?: string;
  rateLimit?: string;
};

/**
 * Editor de integração da API pública (issue #263).
 *
 * Origem: #263 — 2026-08-24 15:39 +0200 (commit 89ca9fd6e, admin de integrações).
 *
 * O que se edita aqui é o ALCANCE de um sistema de terceiro sobre os dados das
 * empresas: quais clientes, quais recursos, de quais IPs, a que ritmo e quem
 * do parceiro enxerga a documentação (#265). A
 * credencial em si não é editável — `clientId`/`clientSecret` não aparecem no
 * formulário, e segredo comprometido se resolve revogando e criando outra.
 *
 * A validação é por campo (`errorText` no `FormField`, não um alerta solto no
 * topo): num formulário deste tamanho, "corrija algo" sem apontar onde obriga
 * o operador a caçar o erro.
 *
 * Clientes (#785): o operador escolhe uma REGRA — Todos / Todos exceto
 * parceiros / Clientes do parceiro — em vez de marcar empresas numa tabela. A
 * regra é avaliada no servidor a cada request (`isTenantInScope` em
 * `apps/functions/src/publicApi/auth.ts`), então cliente novo do parceiro
 * entra sozinho. Sem a tabela de empresas o modal cabe em `large`.
 *
 * Demonstrações com acesso (#785, 2026-10-01): abaixo da regra, a lista de
 * tenants `isDemo` com um checkbox cada. É opcional e é o único caminho de um
 * demo para a API: nenhuma regra alcança demo (`isTenantInScope`), então toda
 * caixa nasce desmarcada e livre, qualquer que seja a regra. Demo gravado que
 * deixou de ser demo
 * aparece num aviso e bloqueia o envio até ser removido — o servidor recusaria
 * (`assertDemoTenantsValid`), e já não dá acesso (`isGrantedDemoTenant`).
 *
 * Usado por: `ApiKeysPage` (`/settings/api-keys`), nos modos criar e editar.
 * Chama `adminCreateApiKey` / `adminUpdateApiKey`; lê `crm_partners` pelo
 * `listCrmPartners` e os demos por `listDemoTenants` (rules: super-admin lê).
 *
 * Histórico:
 *  - 2026-09-30 15:19 +0200 — (#785) regra de clientes no lugar da tabela de empresas.
 *  - 2026-10-01 13:52 +0200 — (#785) seção de demonstrações com acesso.
 *  - 2026-10-01 15:16 +0200 — (#785) caixas de demo não são mais marcadas
 *    nem travadas pela regra ("Todos" marcava todas). Demo só entra marcado,
 *    por decisão do owner; o servidor mudou junto.
 */
export function ApiKeyEditorDialog({
  open,
  mode,
  apiKey,
  onClose,
  onCreated,
}: Props): React.JSX.Element {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [partner, setPartner] = useState('');
  const [partnerSanitized, setPartnerSanitized] = useState(false);
  const [tenantScope, setTenantScope] = useState<ApiTenantScope>(ApiTenantScope.ALL);
  const [scopePartnerId, setScopePartnerId] = useState<string | null>(null);
  const [partners, setPartners] = useState<CrmPartner[]>([]);
  const [partnersState, setPartnersState] = useState<'LOADING' | 'READY' | 'ERROR'>('LOADING');
  const [demoTenantIds, setDemoTenantIds] = useState<string[]>([]);
  const [demoTenants, setDemoTenants] = useState<Tenant[]>([]);
  const [demoIds, setDemoIds] = useState<ReadonlySet<string>>(new Set());
  const [demoState, setDemoState] = useState<'LOADING' | 'READY' | 'ERROR'>('LOADING');
  const [scopes, setScopes] = useState<ApiScope[]>([]);
  const [allowedIps, setAllowedIps] = useState<string[]>([]);
  const [ipDraft, setIpDraft] = useState('');
  const [portalEmails, setPortalEmails] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [requestsPerMinute, setRequestsPerMinute] = useState(
    String(API_DEFAULT_REQUESTS_PER_MINUTE),
  );
  const [active, setActive] = useState(true);
  const [errors, setErrors] = useState<FieldErrors>({});

  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Remove o erro de um campo. Não basta atribuir `undefined`: o tsconfig usa
   * `exactOptionalPropertyTypes`, então a chave precisa sair do objeto.
   */
  function clearError(field: keyof FieldErrors): void {
    setErrors((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => key !== field)),
    );
  }

  // Todos os parceiros, inclusive inativos: parceiro inativo continua com
  // clientes, e a integração que já aponta para ele precisa mostrar o nome.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPartnersState('LOADING');
    listCrmPartners()
      .then((list) => {
        if (cancelled) return;
        setPartners(list);
        setPartnersState('READY');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('ApiKeyEditorDialog listCrmPartners failed', err);
        reportNonFatal('admin_api_key_partners_load_failed', {
          message: err instanceof Error ? err.message : String(err),
        });
        setPartnersState('ERROR');
      });
    return (): void => {
      cancelled = true;
    };
  }, [open]);

  // Demos lidos a cada abertura, como os parceiros: o flag muda pelo admin e
  // uma lista velha esconderia um demo novo ou mostraria um que deixou de ser.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDemoState('LOADING');
    listDemoTenants()
      .then((list) => {
        if (cancelled) return;
        setDemoTenants(list.tenants);
        setDemoIds(list.demoIds);
        setDemoState('READY');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('ApiKeyEditorDialog listDemoTenants failed', err);
        reportNonFatal('admin_api_key_demo_tenants_load_failed', {
          message: err instanceof Error ? err.message : String(err),
        });
        setDemoState('ERROR');
      });
    return (): void => {
      cancelled = true;
    };
  }, [open]);

  // Repovoa a cada abertura: reaproveitar estado antigo mostraria o alcance de
  // outra integração enquanto o formulário "carrega".
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitFailed(false);
    setIpDraft('');
    setPartnerSanitized(false);
    if (apiKey) {
      setName(apiKey.name);
      setPartner(apiKey.partner);
      setTenantScope(apiKey.tenantScope);
      setScopePartnerId(apiKey.scopePartnerId ?? null);
      // `?? []`: integrações anteriores à liberação de demos não têm o campo.
      setDemoTenantIds(apiKey.demoTenantIds ?? []);
      setScopes(apiKey.scopes);
      setAllowedIps(apiKey.allowedIps);
      // `?? []` porque integrações criadas antes da #265 não têm o campo.
      setPortalEmails(apiKey.portalEmails ?? []);
      setRequestsPerMinute(String(apiKey.requestsPerMinute));
      setActive(apiKey.active);
    } else {
      setName('');
      setPartner('');
      setTenantScope(ApiTenantScope.ALL);
      setScopePartnerId(null);
      setDemoTenantIds([]);
      setScopes([]);
      setAllowedIps([]);
      setRequestsPerMinute(String(API_DEFAULT_REQUESTS_PER_MINUTE));
      setActive(true);
    }
  }, [open, apiKey]);

  const partnerOptions = useMemo(
    () => partners.map((p) => ({ value: p.id, label: p.name, tags: [p.countryCode] })),
    [partners],
  );
  // Parceiro gravado que não veio na lista (apagado no CRM) ainda aparece
  // pelo id, para o operador ver o que está salvo em vez de um campo vazio.
  const selectedPartnerOption = useMemo(() => {
    if (scopePartnerId === null) return null;
    return partnerOptions.find((o) => o.value === scopePartnerId) ?? {
      value: scopePartnerId,
      label: scopePartnerId,
    };
  }, [partnerOptions, scopePartnerId]);

  // Ids gravados que não são mais demo (ou foram apagados). Só dá para afirmar
  // isso com a lista carregada.
  // Auditoria 2026-10-01 14:14 +0200 (#785): compara com `demoIds` (todo doc
  // `isDemo` da query), não com `demoTenants` — um demo de doc legado que falha
  // no Zod sumia da lista e virava "deixou de ser demo", travando o salvamento.
  const staleDemoIds = useMemo(() => {
    if (demoState !== 'READY') return [];
    return demoTenantIds.filter((id) => !demoIds.has(id));
  }, [demoState, demoIds, demoTenantIds]);

  /** Marca/desmarca um demo com acesso à API. */
  function toggleDemoTenant(tenantId: string, checked: boolean): void {
    setDemoTenantIds((current) =>
      checked ? [...current, tenantId] : current.filter((id) => id !== tenantId),
    );
    clearError('demoTenants');
  }

  /**
   * O campo aceita só o que o backend aceita (`^[a-z0-9-]+$`): espaço, acento
   * e maiúscula viram hífen/minúscula na digitação, em vez de deixar o
   * operador escrever "Food Gestor" e descobrir o erro só no envio.
   */
  function onPartnerChange(raw: string): void {
    const sanitized = raw
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-{2,}/g, '-');
    setPartnerSanitized(sanitized !== raw);
    setPartner(sanitized);
    clearError('partner');
  }

  /** Valida o rascunho de IP (formato, duplicado, teto `MAX_IPS`) e o adiciona. */
  function addIp(): void {
    const value = ipDraft.trim();
    if (value.length === 0) return;
    if (allowedIps.length >= MAX_IPS) {
      setErrors((e) => ({ ...e, ip: t('admin.apiKeys.fields.ipLimit', { max: MAX_IPS }) }));
      return;
    }
    if (allowedIps.includes(value)) {
      setErrors((e) => ({ ...e, ip: t('admin.apiKeys.fields.ipDuplicate') }));
      return;
    }
    if (!AllowedIpSchema.safeParse(value).success) {
      setErrors((e) => ({ ...e, ip: t('admin.apiKeys.errors.ipInvalid', { value }) }));
      return;
    }
    setAllowedIps([...allowedIps, value]);
    setIpDraft('');
    clearError('ip');
  }

  /**
   * Acrescenta uma conta Google à allowlist do portal de documentação (#265).
   *
   * Normaliza com o MESMO `PortalEmailSchema` que o servidor aplica: o e-mail
   * do token do Firebase Auth chega em minúsculas, então cadastrar
   * "Dev@parceiro.com" cru deixaria a pessoa de fora da própria lista onde ela
   * aparece.
   */
  function addPortalEmail(): void {
    const raw = emailDraft.trim();
    if (raw.length === 0) return;
    if (portalEmails.length >= MAX_PORTAL_EMAILS_PER_KEY) {
      setErrors((e) => ({
        ...e,
        portalEmail: t('admin.apiKeys.fields.portalEmailLimit', { max: MAX_PORTAL_EMAILS_PER_KEY }),
      }));
      return;
    }
    const parsed = PortalEmailSchema.safeParse(raw);
    if (!parsed.success) {
      setErrors((e) => ({
        ...e,
        portalEmail: t('admin.apiKeys.errors.portalEmailInvalid', { value: raw }),
      }));
      return;
    }
    if (portalEmails.includes(parsed.data)) {
      setErrors((e) => ({ ...e, portalEmail: t('admin.apiKeys.fields.portalEmailDuplicate') }));
      return;
    }
    setPortalEmails([...portalEmails, parsed.data]);
    setEmailDraft('');
    clearError('portalEmail');
  }

  /** Marca/desmarca um escopo de recurso. */
  function toggleScope(scope: ApiScope, checked: boolean): void {
    setScopes((current) =>
      checked ? [...current, scope] : current.filter((s) => s !== scope),
    );
    clearError('scopes');
  }

  /** Valida TUDO e devolve os erros por campo — nunca para no primeiro. */
  function validate(): FieldErrors {
    const next: FieldErrors = {};

    const trimmedName = name.trim();
    if (trimmedName.length === 0) next.name = t('admin.apiKeys.errors.nameRequired');
    else if (trimmedName.length > 80) next.name = t('admin.apiKeys.errors.nameTooLong', { max: 80 });

    if (mode === 'create') {
      if (partner.trim().length === 0) next.partner = t('admin.apiKeys.fields.required');
      else if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(partner)) {
        next.partner = t('admin.apiKeys.errors.partnerInvalid');
      }
    }

    if (tenantScope === ApiTenantScope.PARTNER && scopePartnerId === null) {
      next.scopePartner = t('admin.apiKeys.errors.scopePartnerRequired');
    }

    if (staleDemoIds.length > 0) {
      next.demoTenants = t('admin.apiKeys.errors.demoTenantsStale');
    } else if (demoTenantIds.length > MAX_DEMO_TENANTS_PER_KEY) {
      next.demoTenants = t('admin.apiKeys.errors.demoTenantsTooMany', {
        max: MAX_DEMO_TENANTS_PER_KEY,
      });
    }

    if (scopes.length === 0) next.scopes = t('admin.apiKeys.errors.scopesRequired');

    if (allowedIps.length > MAX_IPS) {
      next.ip = t('admin.apiKeys.errors.ipsTooMany', { max: MAX_IPS });
    } else {
      const invalid = allowedIps.find((ip) => !AllowedIpSchema.safeParse(ip).success);
      if (invalid) next.ip = t('admin.apiKeys.errors.ipInvalid', { value: invalid });
      // Um IP digitado e não adicionado seria perdido em silêncio no envio.
      else if (ipDraft.trim().length > 0) next.ip = t('admin.apiKeys.errors.ipNotAdded');
    }

    if (portalEmails.length > MAX_PORTAL_EMAILS_PER_KEY) {
      next.portalEmail = t('admin.apiKeys.errors.portalEmailsTooMany', {
        max: MAX_PORTAL_EMAILS_PER_KEY,
      });
    } else {
      const invalid = portalEmails.find((e) => !PortalEmailSchema.safeParse(e).success);
      if (invalid) next.portalEmail = t('admin.apiKeys.errors.portalEmailInvalid', { value: invalid });
      // Mesmo motivo do IP: e-mail digitado e não adicionado sumiria calado.
      else if (emailDraft.trim().length > 0) {
        next.portalEmail = t('admin.apiKeys.errors.portalEmailNotAdded');
      }
    }

    const rpm = Number(requestsPerMinute);
    if (
      requestsPerMinute.trim().length === 0 ||
      !Number.isInteger(rpm) ||
      rpm < API_MIN_REQUESTS_PER_MINUTE ||
      rpm > API_MAX_REQUESTS_PER_MINUTE
    ) {
      next.rateLimit = t('admin.apiKeys.errors.rateLimitInvalid', {
        min: API_MIN_REQUESTS_PER_MINUTE,
        max: API_MAX_REQUESTS_PER_MINUTE,
      });
    }

    return next;
  }

  /**
   * Valida e chama `adminCreateApiKey` (mostra as credenciais uma única vez) ou
   * `adminUpdateApiKey`. Falha vira o alerta `submitFailed`, sem telemetria.
   */
  async function submit(): Promise<void> {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    // O servidor recusa parceiro sobrando fora de PARTNER (`refineTenantScope`).
    const partnerForScope = tenantScope === ApiTenantScope.PARTNER ? scopePartnerId : null;

    setSubmitFailed(false);
    setSubmitting(true);
    try {
      if (mode === 'create') {
        const credentials = await adminCreateApiKeyCallable({
          name: name.trim(),
          partner: partner.trim(),
          tenantScope,
          scopePartnerId: partnerForScope,
          demoTenantIds,
          scopes,
          allowedIps,
          portalEmails,
          requestsPerMinute: Number(requestsPerMinute),
        });
        toast.success(t('admin.apiKeys.success.created'));
        onCreated(credentials);
      } else if (apiKey) {
        await adminUpdateApiKeyCallable({
          keyId: apiKey.id,
          name: name.trim(),
          tenantScope,
          scopePartnerId: partnerForScope,
          demoTenantIds,
          scopes,
          allowedIps,
          portalEmails,
          requestsPerMinute: Number(requestsPerMinute),
          active,
        });
        toast.success(t('admin.apiKeys.success.updated'));
      }
      onClose();
    } catch (err) {
      console.error('ApiKeyEditorDialog submit failed', err);
      setSubmitFailed(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      onDismiss={onClose}
      // `large`: desde a #785 não há mais a tabela de empresas que exigia
      // `x-large`; o campo mais largo agora é o aviso de IPs.
      size="large"
      header={t(
        mode === 'create' ? 'admin.apiKeys.editor.createTitle' : 'admin.apiKeys.editor.editTitle',
      )}
    >
      <Form
        variant="embedded"
        errorText={submitFailed ? t('admin.apiKeys.errors.submitFailed') : undefined}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" disabled={submitting} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={submitting}
              onClick={(): void => {
                void submit();
              }}
            >
              {t(mode === 'create' ? 'admin.apiKeys.editor.create' : 'admin.apiKeys.editor.save')}
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          <FormField
            label={t('admin.apiKeys.fields.name')}
            errorText={errors.name}
            stretch
          >
            <Input
              value={name}
              onChange={({ detail }): void => {
                setName(detail.value);
                clearError('name');
              }}
              placeholder={t('admin.apiKeys.fields.namePlaceholder')}
              invalid={errors.name !== undefined}
            />
          </FormField>

          {mode === 'create' && (
            <FormField
              label={t('admin.apiKeys.fields.partner')}
              description={t('admin.apiKeys.fields.partnerHint')}
              constraintText={
                partnerSanitized ? t('admin.apiKeys.fields.partnerSanitized') : undefined
              }
              errorText={errors.partner}
              stretch
            >
              <Input
                value={partner}
                onChange={({ detail }): void => {
                  onPartnerChange(detail.value);
                }}
                placeholder={t('admin.apiKeys.fields.partnerPlaceholder')}
                invalid={errors.partner !== undefined}
              />
            </FormField>
          )}

          <FormField
            label={t('admin.apiKeys.fields.tenantScope')}
            description={t('admin.apiKeys.fields.tenantScopeHint')}
            stretch
          >
            <RadioGroup
              value={tenantScope}
              onChange={({ detail }): void => {
                setTenantScope(detail.value as ApiTenantScope);
                clearError('scopePartner');
              }}
              items={[
                {
                  value: ApiTenantScope.ALL,
                  label: t('admin.apiKeys.fields.tenantScopeAll'),
                  description: t('admin.apiKeys.fields.tenantScopeAllHint'),
                },
                {
                  value: ApiTenantScope.NON_PARTNER,
                  label: t('admin.apiKeys.fields.tenantScopeNonPartner'),
                  description: t('admin.apiKeys.fields.tenantScopeNonPartnerHint'),
                },
                {
                  value: ApiTenantScope.PARTNER,
                  label: t('admin.apiKeys.fields.tenantScopePartner'),
                  description: t('admin.apiKeys.fields.tenantScopePartnerHint'),
                },
              ]}
            />
          </FormField>

          {tenantScope === ApiTenantScope.PARTNER && (
            <FormField
              label={t('admin.apiKeys.fields.scopePartner')}
              errorText={
                errors.scopePartner ??
                (partnersState === 'ERROR' ? t('admin.apiKeys.errors.partnersLoad') : undefined)
              }
              stretch
            >
              <Select
                selectedOption={selectedPartnerOption}
                onChange={({ detail }): void => {
                  setScopePartnerId(detail.selectedOption.value ?? null);
                  clearError('scopePartner');
                }}
                options={partnerOptions}
                filteringType="auto"
                placeholder={t('admin.apiKeys.fields.scopePartnerPlaceholder')}
                empty={t('admin.apiKeys.fields.scopePartnerEmpty')}
                statusType={partnersState === 'LOADING' ? 'loading' : 'finished'}
                invalid={errors.scopePartner !== undefined}
              />
            </FormField>
          )}

          <FormField
            label={t('admin.apiKeys.fields.demoTenants')}
            description={t('admin.apiKeys.fields.demoTenantsHint')}
            errorText={
              errors.demoTenants ??
              (demoState === 'ERROR' ? t('admin.apiKeys.errors.demoTenantsLoad') : undefined)
            }
            stretch
          >
            <SpaceBetween size="xs">
              {staleDemoIds.length > 0 && (
                <Alert type="warning" header={t('admin.apiKeys.fields.demoTenantsStaleHeader')}>
                  <SpaceBetween size="xs">
                    <span>{t('admin.apiKeys.fields.demoTenantsStale')}</span>
                    <TokenGroup
                      items={staleDemoIds.map((id) => ({ label: id, dismissLabel: id }))}
                      onDismiss={({ detail }): void => {
                        const id = staleDemoIds[detail.itemIndex];
                        if (id !== undefined) toggleDemoTenant(id, false);
                      }}
                    />
                  </SpaceBetween>
                </Alert>
              )}
              {demoState === 'LOADING' && (
                <Box color="text-status-inactive" fontSize="body-s">
                  {t('admin.apiKeys.fields.demoTenantsLoading')}
                </Box>
              )}
              {demoState === 'READY' && demoTenants.length === 0 && (
                <Box color="text-status-inactive" fontSize="body-s">
                  {t('admin.apiKeys.fields.demoTenantsEmpty')}
                </Box>
              )}
              {demoState === 'READY' &&
                // Sempre livre e marcado só pelo operador (#785, owner
                // 2026-10-01 15:16 +0200): a regra não alcança demo, então
                // não há o que mostrar como "já incluído". A versão anterior
                // marcava e travava todos em "Todos".
                demoTenants.map((tenant) => (
                  <Checkbox
                    key={tenant.id}
                    checked={demoTenantIds.includes(tenant.id)}
                    onChange={({ detail }): void => {
                      toggleDemoTenant(tenant.id, detail.checked);
                    }}
                  >
                    {tenant.countryCode ? `${tenant.name} (${tenant.countryCode})` : tenant.name}
                  </Checkbox>
                ))}
            </SpaceBetween>
          </FormField>

          <FormField
            label={t('admin.apiKeys.fields.scopes')}
            description={t('admin.apiKeys.fields.scopesHint')}
            errorText={errors.scopes}
            stretch
          >
            <SpaceBetween size="xs">
              {ALL_API_SCOPES.map((scope) => (
                <Checkbox
                  key={scope}
                  checked={scopes.includes(scope)}
                  onChange={({ detail }): void => {
                    toggleScope(scope, detail.checked);
                  }}
                >
                  {`${t(scopeKey(scope))} — ${t('admin.apiKeys.fields.scopeSuffixRead')}`}
                </Checkbox>
              ))}
            </SpaceBetween>
          </FormField>

          <FormField
            label={t('admin.apiKeys.fields.allowedIps')}
            description={t('admin.apiKeys.fields.allowedIpsHint')}
            errorText={errors.ip}
            stretch
          >
            <SpaceBetween size="xs">
              {/*
                A allowlist funciona pela URL nativa da API, mas NÃO por
                `api.etiquetachef.com`: o rewrite de Hosting faz a origem chegar
                como IP do edge do Google, e a lista passaria a negar o parceiro
                legítimo (#268). O aviso fica aqui, no ponto de decisão, porque
                quem preenche este campo é quem sofre a consequência — deixá-lo
                só na spec garantiria que ninguém lesse a tempo.
              */}
              <Alert type="warning" header={t('admin.apiKeys.fields.allowedIpsPendingHeader')}>
                {t('admin.apiKeys.fields.allowedIpsPending')}
              </Alert>
              <SpaceBetween direction="horizontal" size="xs">
                <Input
                  value={ipDraft}
                  onChange={({ detail }): void => {
                    setIpDraft(detail.value);
                    clearError('ip');
                  }}
                  onKeyDown={({ detail }): void => {
                    if (detail.key === 'Enter') addIp();
                  }}
                  placeholder={t('admin.apiKeys.fields.ipPlaceholder')}
                  invalid={errors.ip !== undefined}
                />
                <Button disabled={ipDraft.trim().length === 0} onClick={addIp}>
                  {t('admin.apiKeys.fields.ipAdd')}
                </Button>
              </SpaceBetween>
              {allowedIps.length === 0 ? (
                <Box color="text-status-inactive" fontSize="body-s">
                  {t('admin.apiKeys.fields.ipEmpty')}
                </Box>
              ) : (
                <TokenGroup
                  items={allowedIps.map((ip) => ({ label: ip, dismissLabel: ip }))}
                  onDismiss={({ detail }): void => {
                    setAllowedIps(allowedIps.filter((_, i) => i !== detail.itemIndex));
                  }}
                />
              )}
            </SpaceBetween>
          </FormField>

          <FormField
            label={t('admin.apiKeys.fields.portalEmails')}
            description={t('admin.apiKeys.fields.portalEmailsHint')}
            errorText={errors.portalEmail}
            stretch
          >
            <SpaceBetween size="xs">
              <SpaceBetween direction="horizontal" size="xs">
                <Input
                  value={emailDraft}
                  type="email"
                  onChange={({ detail }): void => {
                    setEmailDraft(detail.value);
                    clearError('portalEmail');
                  }}
                  onKeyDown={({ detail }): void => {
                    if (detail.key === 'Enter') addPortalEmail();
                  }}
                  placeholder={t('admin.apiKeys.fields.portalEmailPlaceholder')}
                  invalid={errors.portalEmail !== undefined}
                />
                <Button disabled={emailDraft.trim().length === 0} onClick={addPortalEmail}>
                  {t('admin.apiKeys.fields.portalEmailAdd')}
                </Button>
              </SpaceBetween>
              {portalEmails.length === 0 ? (
                <Box color="text-status-inactive" fontSize="body-s">
                  {t('admin.apiKeys.fields.portalEmailEmpty')}
                </Box>
              ) : (
                <TokenGroup
                  items={portalEmails.map((email) => ({ label: email, dismissLabel: email }))}
                  onDismiss={({ detail }): void => {
                    setPortalEmails(portalEmails.filter((_, i) => i !== detail.itemIndex));
                  }}
                />
              )}
            </SpaceBetween>
          </FormField>

          <FormField
            label={t('admin.apiKeys.fields.rateLimit')}
            description={t('admin.apiKeys.fields.rateLimitHint', {
              min: API_MIN_REQUESTS_PER_MINUTE,
              max: API_MAX_REQUESTS_PER_MINUTE,
            })}
            errorText={errors.rateLimit}
            stretch
          >
            <Input
              type="number"
              inputMode="numeric"
              value={requestsPerMinute}
              onChange={({ detail }): void => {
                setRequestsPerMinute(detail.value);
                clearError('rateLimit');
              }}
              invalid={errors.rateLimit !== undefined}
            />
          </FormField>

          {mode === 'edit' && (
            <FormField
              label={t('admin.apiKeys.fields.active')}
              description={t('admin.apiKeys.fields.activeHint')}
              stretch
            >
              <Toggle
                checked={active}
                onChange={({ detail }): void => {
                  setActive(detail.checked);
                }}
              >
                {t(active ? 'admin.apiKeys.status.active' : 'admin.apiKeys.status.paused')}
              </Toggle>
            </FormField>
          )}

          {Object.keys(errors).length > 0 && (
            <Alert type="error">{t('admin.apiKeys.fields.fixErrors')}</Alert>
          )}
        </SpaceBetween>
      </Form>
    </Modal>
  );
}
